import { apiJson } from './apiFetch';
import type { CanvasserAnalyticsFilters } from '../../lib/queryKeys';
import { campaignHeaders, defineQuery } from '../../lib/data';
import type { CanvasserStatsResponse, VendorSummaryItem, MyStatsResponse, CanvasserPerformanceResponse, VendorPerformanceResponse } from '@signalvote/shared';
import { buildQueryString } from './queryUtils';

type DateFilters = Pick<CanvasserAnalyticsFilters, 'dateFrom' | 'dateTo'>;

function getAnalytics<T>(path: string, qs: string, error: string, signal: AbortSignal, campaignId: string | null): Promise<T> {
  return apiJson<T>(`/api/analytics/${path}${qs}`, { signal, headers: campaignHeaders(campaignId), error });
}

const canvasserStats = defineQuery({
  key: (filters: CanvasserAnalyticsFilters | undefined) => ['analytics', 'canvasserStats', filters ?? null],
  reads: 'GET /api/Analytics/canvasser-stats',
  staleTime: 2 * 60 * 1000,
  fetch: (filters, { signal, campaignId }) =>
    getAnalytics<CanvasserStatsResponse>(
      'canvasser-stats',
      buildQueryString({
        type: filters?.type,
        vendorId: filters?.vendorId,
        teamId: filters?.teamId,
        userId: filters?.userId,
        dateFrom: filters?.dateFrom,
        dateTo: filters?.dateTo,
      }),
      'Failed to fetch canvasser stats',
      signal,
      campaignId,
    ),
});

export function useCanvasserStats(filters?: CanvasserAnalyticsFilters) {
  return canvasserStats(filters);
}

const vendorSummary = defineQuery({
  key: (filters: CanvasserAnalyticsFilters | undefined) => ['analytics', 'vendorSummary', filters ?? null],
  reads: 'GET /api/Analytics/vendor-summary',
  staleTime: 2 * 60 * 1000,
  fetch: (filters, { signal, campaignId }) =>
    getAnalytics<VendorSummaryItem[]>(
      'vendor-summary',
      buildQueryString({
        type: filters?.type,
        vendorId: filters?.vendorId,
        teamId: filters?.teamId,
        dateFrom: filters?.dateFrom,
        dateTo: filters?.dateTo,
      }),
      'Failed to fetch vendor summary',
      signal,
      campaignId,
    ),
});

export function useVendorSummary(filters?: CanvasserAnalyticsFilters) {
  return vendorSummary(filters);
}

const myStats = defineQuery({
  key: (filters: DateFilters | undefined) => ['analytics', 'myStats', filters ?? null],
  reads: 'GET /api/Analytics/my-stats',
  fetch: (filters, { signal, campaignId }) =>
    getAnalytics<MyStatsResponse>(
      'my-stats',
      buildQueryString({ dateFrom: filters?.dateFrom, dateTo: filters?.dateTo }),
      'Failed to fetch my stats',
      signal,
      campaignId,
    ),
});

export function useMyStats(filters?: DateFilters) {
  return myStats(filters);
}

export const useCanvasserPerformance = defineQuery({
  key: (userId: string) => ['analytics', 'canvasserPerformance', userId],
  reads: 'GET /api/Analytics/canvasser-performance',
  staleTime: 10 * 60 * 1000,
  fetch: (userId, { signal, campaignId }) =>
    getAnalytics<CanvasserPerformanceResponse>('canvasser-performance', buildQueryString({ userId }), 'Failed to fetch canvasser performance', signal, campaignId),
});

const vendorPerformance = defineQuery({
  key: ({ vendorId, filters }: { vendorId: string; filters?: DateFilters }) => ['analytics', 'vendorPerformance', vendorId, filters ?? null],
  reads: 'GET /api/Analytics/vendor-performance',
  staleTime: 10 * 60 * 1000,
  fetch: ({ vendorId, filters }, { signal, campaignId }) =>
    getAnalytics<VendorPerformanceResponse>(
      'vendor-performance',
      buildQueryString({ vendorId, dateFrom: filters?.dateFrom, dateTo: filters?.dateTo }),
      'Failed to fetch vendor performance',
      signal,
      campaignId,
    ),
});

export function useVendorPerformance(vendorId: string, filters?: DateFilters) {
  return vendorPerformance({ vendorId, filters });
}
